import React, { useState, useEffect } from 'react';
import { Invoice } from '../types';
import { 
  DocumentTextIcon, 
  CalendarIcon, 
  UserIcon,
  CurrencyEuroIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { formatIban } from '../utils/spanishFiscal';
import { isDanielEntikCompany } from '../utils/entikRecordsAccess';
import { apiService } from '../services/apiService';
import { InvoicePdfPreviewModal } from './InvoicePdfPreviewModal';

interface CompanyInfo {
  name: string;
  description: string;
  address: string;
  taxId?: string;
  iban?: string;
  bankName?: string;
  email: string;
  phone: string;
  footerMessage: string;
  footerSubMessage: string;
}

interface InvoicePreviewProps {
  invoice: Invoice;
  onClose: () => void;
  onEdit?: (invoice: Invoice) => void;
  companyInfo?: CompanyInfo;
}

export const InvoicePreview: React.FC<InvoicePreviewProps> = ({ invoice, onClose, onEdit, companyInfo }) => {
  const invoiceGroups = invoice.groups || [];
  const [isExportingAeat, setIsExportingAeat] = useState(false);
  const [isSubmittingAeat, setIsSubmittingAeat] = useState(false);
  const [vfLoading, setVfLoading] = useState(false);
  const [vfRemitLoading, setVfRemitLoading] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [vfRegistro, setVfRegistro] = useState<{
    huella?: string;
    qrUrl?: string;
    qrPngDataUrl?: string;
    cadenaHuella?: string;
    fechaHoraGen?: string;
    remision?: {
      estado?: string;
      csv?: string;
      codigoError?: string;
      descripcion?: string;
      remitidoAt?: string;
    };
  } | null>(null);
  // Datos por defecto si no se proporciona companyInfo
  const defaultCompanyInfo: CompanyInfo = {
    name: 'Daniel Vázquez Moreno',
    description: 'Servicios profesionales de desarrollo web',
    address: 'C/ Valmojado 111 3ºB, Madrid',
    taxId: '',
    iban: '',
    bankName: '',
    email: 'daniel@facturacion.com',
    phone: '+34 600 000 000',
    footerMessage: 'Gracias por confiar en nuestros servicios',
    footerSubMessage: 'Para cualquier consulta, no dude en contactarnos'
  };

  // Cargar desde localStorage si no se proporciona companyInfo
  const getCompanyInfo = (): CompanyInfo => {
    if (companyInfo) return companyInfo;
    
    const saved = localStorage.getItem('companyInfo');
    return saved ? JSON.parse(saved) : defaultCompanyInfo;
  };

  const currentCompanyInfo = getCompanyInfo();
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateShort = (date: Date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const referenceCode = `${String(new Date(invoice.date).getFullYear()).slice(-2)}_${String(invoice.number).padStart(3, '0')}`;

  const conceptLines: { label?: string; description: string; importe: number }[] = [
    ...(invoice.services || []).map((s) => ({
      description: s.description,
      importe: Number(s.importe ?? s.unitPrice ?? 0),
    })),
    ...(invoice.reimbursableExpenses || []).map((g) => ({
      label: 'Gasto no suplido',
      description: g.description,
      importe: Number(g.importe ?? 0),
    })),
    ...(invoice.suplidos || []).map((s) => ({
      label: 'Suplido',
      description: s.description,
      importe: Number(s.importe ?? 0),
    })),
  ];
  if (!conceptLines.length && invoice.description) {
    conceptLines.push({
      description: invoice.description,
      importe: Number(invoice.subtotal || 0),
    });
  }
  const isDanielCompany = isDanielEntikCompany({
    id: '',
    cif: currentCompanyInfo.taxId || '',
    name: currentCompanyInfo.name,
  } as any);
  const isExpenseTemplate =
    invoice.templateType === 'expense' ||
    invoice.templateType === 'expense_suplidos' ||
    isDanielCompany;
  const isExpenseSuplidos = invoice.templateType === 'expense_suplidos';
  const isExpenseNoSuplidos = invoice.templateType === 'expense';
  const isCorporateTemplate = invoice.templateType === 'corporate';
  const isCleanTemplate = invoice.templateType === 'clean';
  const isHistoricAeat = !!(invoice as any).alreadyPresentedToAeat;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'paid':
        return 'Pagada';
      case 'sent':
        return 'Enviada';
      case 'overdue':
        return 'Vencida';
      default:
        return 'Borrador';
    }
  };

  const handleExportAeatXml = async () => {
    if (!invoice.id) {
      alert('La factura debe estar guardada antes de exportarla a AEAT');
      return;
    }
    setIsExportingAeat(true);
    try {
      const response = await apiService.prepareAeatInvoice(invoice.id);
      if (response.error || !response.data) {
        alert(response.message || response.error || 'No se pudo preparar el XML de AEAT');
        return;
      }
      const payload = response.data as any;
      const xml = payload?.facturae?.xml;
      if (!xml) {
        alert('No se genero el XML Facturae');
        return;
      }
      const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `factura-${invoice.number}-aeat-facturae.xml`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Error preparando exportacion AEAT');
    } finally {
      setIsExportingAeat(false);
    }
  };

  useEffect(() => {
    if (!invoice.id) return;
    let cancelled = false;
    (async () => {
      const response = await apiService.getVerifactuRegistro(invoice.id!);
      if (cancelled) return;
      if (!response.error && response.data && (response.data as any).registro) {
        setVfRegistro((response.data as any).registro);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invoice.id]);

  const handleVerifactuRegistro = async () => {
    if (!invoice.id) {
      alert('La factura debe estar guardada antes de generar VeriFactu');
      return;
    }
    setVfLoading(true);
    try {
      const response = await apiService.createVerifactuRegistro(invoice.id);
      if (response.error || !(response.data as any)?.registro) {
        alert(response.message || response.error || 'No se pudo generar el registro VeriFactu');
        return;
      }
      const data = response.data as any;
      setVfRegistro(data.registro);
      if (data.remision?.estado) {
        const st = data.remision.estado;
        const csv = data.remision.csv ? ` CSV: ${data.remision.csv}` : '';
        alert(`Registro generado. Remisión AEAT: ${st}${csv}`);
      }
    } catch {
      alert('Error generando registro VeriFactu');
    } finally {
      setVfLoading(false);
    }
  };

  const handleVerifactuRemitir = async () => {
    if (!invoice.id) {
      alert('La factura debe estar guardada');
      return;
    }
    if (!vfRegistro?.huella) {
      alert('Genera primero el registro VeriFactu (huella + QR)');
      return;
    }
    setVfRemitLoading(true);
    try {
      const response = await apiService.remitVerifactuRegistro(invoice.id);
      if (response.error) {
        alert(response.message || response.error || 'No se pudo remitir a la AEAT');
        return;
      }
      const data = response.data as any;
      if (data.registro) setVfRegistro(data.registro);
      const rem = data.remision;
      const msg = rem
        ? `Estado: ${rem.estado}${rem.csv ? `\nCSV: ${rem.csv}` : ''}${rem.descripcionError ? `\n${rem.descripcionError}` : ''}`
        : data.message || 'Remisión procesada';
      alert(msg);
    } catch {
      alert('Error en la remisión VeriFactu');
    } finally {
      setVfRemitLoading(false);
    }
  };

  const handleSubmitAeat = async () => {
    if (!invoice.id) {
      alert('La factura debe estar guardada antes de enviarla a AEAT');
      return;
    }
    setIsSubmittingAeat(true);
    try {
      const response = await apiService.submitAeatInvoice(invoice.id);
      if (response.error) {
        alert(response.message || response.error || 'No se pudo enviar a AEAT');
        return;
      }
      alert('Solicitud AEAT enviada. Revisa el resultado en el backend.');
    } catch {
      alert('Error enviando factura a AEAT');
    } finally {
      setIsSubmittingAeat(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <DocumentTextIcon className="h-8 w-8 text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Vista Previa de Factura #{invoice.number}
              </h2>
              <p className="text-gray-600">Cliente: {invoice.client.name}</p>
              {invoiceGroups.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {invoiceGroups.map((g) => (
                    <span
                      key={g.id}
                      className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{
                        color: g.color || '#635BFF',
                        backgroundColor: `${g.color || '#635BFF'}15`,
                        border: `1px solid ${g.color || '#635BFF'}30`
                      }}
                    >
                      {g.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Invoice Content */}
        <div className={`p-8 ${isCorporateTemplate ? 'bg-slate-50' : 'bg-white'}`}>
          {isCorporateTemplate && (
            <div className="mb-6 rounded-md bg-slate-800 text-white px-4 py-3">
              <p className="text-sm tracking-wide uppercase">Plantilla corporativa</p>
            </div>
          )}
          {isExpenseTemplate ? (
            <div className="text-[15px] text-gray-900">
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div className="space-y-1">
                  <div><span className="font-medium">Numero:</span> {invoice.number}</div>
                  <div><span className="font-medium">Fecha:</span> {formatDateShort(invoice.date)}</div>
                </div>
                <div className="text-right space-y-1">
                  <div className="font-semibold">{currentCompanyInfo.name}</div>
                  <div>{currentCompanyInfo.address}</div>
                  <div>{currentCompanyInfo.taxId ? `NIF ${currentCompanyInfo.taxId}` : ''}</div>
                </div>
              </div>

              <div className="border border-gray-700 mb-6">
                <div className="grid grid-cols-5">
                  <div className="col-span-3 border-r border-gray-700 p-3">
                    <div><span className="font-medium">Cliente:</span> {invoice.client.name}</div>
                    <div><span className="font-medium">Domicilio:</span> {invoice.client.address}</div>
                    <div><span className="font-medium">Ciudad:</span> {invoice.client.city}</div>
                    <div><span className="font-medium">CIF:</span> {invoice.client.cif}</div>
                  </div>
                  <div className="col-span-2 p-3">
                    <div className="font-medium">Numero de cuenta</div>
                    <div>
                      {(currentCompanyInfo.bankName || '').trim() ? `${currentCompanyInfo.bankName} ` : ''}
                      {formatIban(invoice.bankIban || currentCompanyInfo.iban || '')}
                    </div>
                  </div>
                </div>
              </div>

              <div className="border border-gray-700 mb-0">
                <div className="grid grid-cols-12 border-b border-gray-700">
                  <div className="col-span-2 p-2 font-medium">{referenceCode}</div>
                  <div className="col-span-8 p-2">{invoice.description}</div>
                  <div className="col-span-2 p-2 text-right">{formatCurrency(invoice.subtotal)}</div>
                </div>
                <div className="h-[360px]"></div>
              </div>

              <div className="border border-t-0 border-gray-700">
                <div className="grid grid-cols-12">
                  <div className="col-span-2 border-r border-gray-700 min-h-[160px]"></div>
                  <div className="col-span-10">
                    <div className="grid grid-cols-12 border-b border-gray-300">
                      <div className="col-span-8 p-2 font-semibold text-center">Base imponible</div>
                      <div className="col-span-4 p-2 text-right">{formatCurrency(invoice.subtotal)}</div>
                    </div>
                    <div className="grid grid-cols-12 border-b border-gray-300">
                      <div className="col-span-6 p-2 font-semibold text-right">IVA</div>
                      <div className="col-span-2 p-2 text-center">{invoice.ivaPercentage}%</div>
                      <div className="col-span-4 p-2 text-right">{formatCurrency(invoice.ivaAmount)}</div>
                    </div>
                    <div className="grid grid-cols-12 border-b border-gray-300">
                      <div className="col-span-6 p-2 font-semibold text-right">IRPF</div>
                      <div className="col-span-2 p-2 text-center">{invoice.irpfPercentage}%</div>
                      <div className="col-span-4 p-2 text-right">{formatCurrency(invoice.irpfAmount)}</div>
                    </div>
                {!!invoice.suplidosAmount && !isExpenseNoSuplidos && (
                  <div className="grid grid-cols-12 border-b border-gray-300">
                    <div className="col-span-8 p-2 font-semibold text-right">Suplidos repercutidos</div>
                    <div className="col-span-4 p-2 text-right">{formatCurrency(invoice.suplidosAmount)}</div>
                  </div>
                )}
                    <div className="grid grid-cols-12 bg-gray-100">
                      <div className="col-span-8 p-2 font-bold text-right">TOTAL FACTURA</div>
                      <div className="col-span-4 p-2 font-bold text-right">{formatCurrency(invoice.total)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
          <>
          {/* Encabezado de la factura */}
          <div className="flex justify-between items-start mb-8">
            {/* Datos del emisor (izquierda) */}
            <div className="text-left">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{currentCompanyInfo.name}</h1>
              <p className="text-gray-700">{currentCompanyInfo.address}</p>
              <p className="text-gray-700">{currentCompanyInfo.email}</p>
              {currentCompanyInfo.taxId ? (
                <p className="text-gray-700">NIF/CIF: {currentCompanyInfo.taxId}</p>
              ) : null}
            </div>

            {/* Datos de la factura (derecha) */}
            <div className="text-right">
              <div className="mb-4">
                <p className="text-sm text-gray-600">Número</p>
                <p className="text-xl font-bold text-gray-900">{invoice.number}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Fecha</p>
                <p className="text-lg font-semibold text-gray-900">{formatDate(invoice.date)}</p>
              </div>
            </div>
          </div>

          {/* Datos del cliente y cuenta bancaria */}
          <div className="flex justify-between items-start mb-8">
            {/* Datos del cliente (izquierda) */}
            <div className="text-left">
              <p className="text-sm text-gray-600 mb-1">Nombre</p>
              <p className="text-lg font-semibold text-gray-900 mb-2">{invoice.client.name}</p>
              
              <p className="text-sm text-gray-600 mb-1">Domicilio</p>
              <p className="text-gray-900 mb-1">{invoice.client.address}</p>
              <p className="text-gray-900 mb-2">{invoice.client.city}</p>
              
              <p className="text-sm text-gray-600 mb-1">CIF</p>
              <p className="text-gray-900">{invoice.client.cif || '53989846Z'}</p>
            </div>

            {/* Cuenta bancaria (derecha) */}
            <div className="text-right">
              <p className="text-sm text-gray-600 mb-1">Metodo de pago</p>
              <p className="text-gray-900 mb-2">
                {invoice.paymentMethod === 'card' && 'Tarjeta'}
                {invoice.paymentMethod === 'cash' && 'Efectivo'}
                {invoice.paymentMethod === 'direct_debit' && 'Domiciliacion'}
                {invoice.paymentMethod === 'bizum' && 'Bizum'}
                {!invoice.paymentMethod || invoice.paymentMethod === 'transfer' ? 'Transferencia bancaria' : ''}
              </p>
              <p className="text-sm text-gray-600 mb-1">Vencimiento</p>
              <p className="text-gray-900 mb-2">
                {invoice.dueDate ? formatDate(invoice.dueDate) : 'Al contado'}
              </p>
              {invoice.paymentMethod === 'transfer' && (invoice.bankIban || currentCompanyInfo.iban) ? (
                <>
                  {invoice.bankHolder ? (
                    <>
                      <p className="text-sm text-gray-600 mb-1">Titular</p>
                      <p className="text-gray-900 mb-2">{invoice.bankHolder}</p>
                    </>
                  ) : null}
                  {(invoice.bankName || currentCompanyInfo.bankName) ? (
                    <>
                      <p className="text-sm text-gray-600 mb-1">Banco</p>
                      <p className="text-gray-900 mb-2">{invoice.bankName || currentCompanyInfo.bankName}</p>
                    </>
                  ) : null}
                  <p className="text-sm text-gray-600 mb-1">IBAN</p>
                  <p className="text-gray-900 mb-2">{formatIban(invoice.bankIban || currentCompanyInfo.iban || '')}</p>
                  {invoice.paymentNotes ? (
                    <>
                      <p className="text-sm text-gray-600 mb-1">Concepto</p>
                      <p className="text-gray-900 mb-2">{invoice.paymentNotes}</p>
                    </>
                  ) : null}
                </>
              ) : null}
              {invoice.paymentMethod === 'bizum' && invoice.paymentNotes ? (
                <>
                  <p className="text-sm text-gray-600 mb-1">Bizum</p>
                  <p className="text-gray-900 mb-2">{invoice.paymentNotes}</p>
                </>
              ) : null}
              {invoice.paymentMethod === 'card' && invoice.paymentNotes ? (
                <p className="text-gray-900 mb-2">{invoice.paymentNotes}</p>
              ) : null}
            </div>
          </div>

          {/* Conceptos / líneas */}
          <div className="mb-8 space-y-2">
            {conceptLines.map((line, idx) => (
              <div
                key={idx}
                className="flex justify-between items-start gap-4 py-3 border-b border-gray-200"
              >
                <div className="flex-1 min-w-0">
                  {line.label && (
                    <span className="text-xs font-medium text-amber-700 dark:text-amber-400 block mb-0.5">
                      {line.label}
                    </span>
                  )}
                  <p className="text-gray-900 whitespace-pre-wrap">{line.description}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-lg font-semibold text-gray-900">
                    {formatCurrency(line.importe)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Tabla de resumen */}
          <div className="mb-8">
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-300">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-700">Concepto</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-700">Importe</p>
                  </div>
                </div>
              </div>
              
              <div className="px-4 py-3 border-b border-gray-300">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-left">
                    <p className="text-gray-900">Subtotal</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-900">{formatCurrency(invoice.subtotal)}</p>
                  </div>
                </div>
              </div>
              
              <div className="px-4 py-3 border-b border-gray-300">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-left">
                    <p className="text-gray-900">IVA ({invoice.ivaPercentage}%)</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-900">{formatCurrency(invoice.ivaAmount)}</p>
                  </div>
                </div>
              </div>
              
              <div className="px-4 py-3 border-b border-gray-300">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-left">
                    <p className="text-gray-900">IRPF ({invoice.irpfPercentage}%)</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-900">{formatCurrency(invoice.irpfAmount)}</p>
                  </div>
                </div>
              </div>
              {!!invoice.suplidosAmount && !isExpenseNoSuplidos && (
                <div className="px-4 py-3 border-b border-gray-300">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-left">
                      <p className="text-gray-900">Suplidos repercutidos</p>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-900">{formatCurrency(invoice.suplidosAmount)}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="px-4 py-3 bg-gray-100">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-left">
                    <p className="text-lg font-bold text-gray-900">TOTAL FACTURA</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">{formatCurrency(invoice.total)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notas */}
          {invoice.notes && (
            <div className="mb-8">
              <p className="text-sm text-gray-600">{invoice.notes}</p>
            </div>
          )}

          {(invoice.templateType === 'expense' || invoice.templateType === 'expense_suplidos') && (
            <div className={`mb-4 text-sm text-gray-600 ${isCleanTemplate ? '' : 'border rounded-lg p-3 bg-gray-50'}`}>
              {isExpenseSuplidos
                ? 'Factura con gastos suplidos repercutidos. Los suplidos no forman parte de la base imponible ni llevan IVA ni IRPF.'
                : 'Factura de gastos sin suplidos. Todos los importes figuran en la base imponible.'}
            </div>
          )}

          {(invoice.templateType === 'expense' || invoice.templateType === 'expense_suplidos') && (
            <div className={`mb-8 grid grid-cols-1 md:grid-cols-3 gap-4 ${isCleanTemplate ? '' : 'border rounded-lg p-3'}`}>
              <div>
                <p className="text-xs text-gray-500">Categoria de gasto</p>
                <p className="text-sm text-gray-800">
                  {invoice.expenseCategory === 'travel' && 'Desplazamiento'}
                  {invoice.expenseCategory === 'supplies' && 'Material / suministros'}
                  {invoice.expenseCategory === 'services' && 'Servicios de terceros'}
                  {invoice.expenseCategory === 'fees' && 'Tasas / honorarios'}
                  {!invoice.expenseCategory || invoice.expenseCategory === 'other' ? 'Otros' : ''}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Referencia</p>
                <p className="text-sm text-gray-800">{invoice.expenseReference || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Deducible</p>
                <p className="text-sm text-gray-800">{invoice.deductibleExpense === false ? 'No' : 'Si'}</p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center text-sm text-gray-500 border-t border-gray-200 pt-6">
            <p>{currentCompanyInfo.footerMessage}</p>
            <p className="mt-1">{currentCompanyInfo.footerSubMessage}</p>
          </div>
          </>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3 p-6 border-t border-gray-200 bg-gray-50">
          {isHistoricAeat ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-medium">Factura histórica</p>
              <p className="text-xs mt-1">
                Marcada como ya presentada a Hacienda. AEAT/VeriFactu están deshabilitados para evitar duplicidades.
              </p>
              {(invoice as any).alreadyPresentedRef ? (
                <p className="text-xs mt-1">
                  Referencia: <span className="font-mono">{String((invoice as any).alreadyPresentedRef)}</span>
                </p>
              ) : null}
            </div>
          ) : null}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleVerifactuRegistro}
              disabled={vfLoading || vfRemitLoading || isHistoricAeat}
              className="px-4 py-2 text-sm font-medium text-white bg-violet-700 border border-transparent rounded-md hover:bg-violet-800 disabled:opacity-60"
            >
              {vfLoading ? 'VeriFactu...' : vfRegistro?.huella ? 'VeriFactu (ya generado)' : 'Generar VeriFactu (huella + QR)'}
            </button>
            <button
              type="button"
              onClick={handleVerifactuRemitir}
              disabled={!vfRegistro?.huella || vfRemitLoading || vfLoading || isHistoricAeat}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-900 border border-transparent rounded-md hover:bg-purple-950 disabled:opacity-60"
              title="Remisión voluntaria SOAP a la AEAT (requiere certificado en el servidor)"
            >
              {vfRemitLoading ? 'Remitiendo...' : 'Remitir a AEAT'}
            </button>
            <button
              onClick={handleSubmitAeat}
              disabled={isSubmittingAeat || isHistoricAeat}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-60"
            >
              {isSubmittingAeat ? 'Enviando AEAT...' : 'Enviar AEAT directo'}
            </button>
            <button
              onClick={handleExportAeatXml}
              disabled={isExportingAeat}
              className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-md hover:bg-emerald-700 disabled:opacity-60"
            >
              {isExportingAeat ? 'Preparando AEAT...' : 'Exportar AEAT (Facturae XML)'}
            </button>
            {invoice.id ? (
              <button
                type="button"
                onClick={() => setPdfPreviewOpen(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700"
              >
                Vista previa PDF
              </button>
            ) : null}
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cerrar
            </button>
            {onEdit && (
              <button
                onClick={() => onEdit(invoice)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Editar Factura
              </button>
            )}
          </div>
          {vfRegistro?.huella ? (
            <div className="flex flex-wrap items-start gap-4 justify-end border-t border-gray-200 pt-3">
              {vfRegistro.qrPngDataUrl ? (
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">QR tributario</p>
                  <img src={vfRegistro.qrPngDataUrl} alt="QR VeriFactu" className="w-28 h-28 border border-gray-200 rounded" />
                </div>
              ) : null}
              <div className="text-right text-xs text-gray-600 max-w-lg">
                <p className="font-semibold text-gray-800">VeriFactu — huella encadenada</p>
                {vfRegistro.remision?.estado ? (
                  <p className="mt-1">
                    <span className="font-medium">Remisión AEAT:</span>{' '}
                    <span
                      className={
                        vfRegistro.remision.estado === 'aceptado'
                          ? 'text-green-700'
                          : vfRegistro.remision.estado === 'rechazado' || vfRegistro.remision.estado === 'error'
                            ? 'text-red-700'
                            : 'text-amber-700'
                      }
                    >
                      {vfRegistro.remision.estado}
                    </span>
                    {vfRegistro.remision.csv ? (
                      <span className="font-mono ml-1">CSV {vfRegistro.remision.csv}</span>
                    ) : null}
                  </p>
                ) : (
                  <p className="text-[10px] text-amber-700 mt-1">Remisión pendiente — configura certificado en el servidor.</p>
                )}
                <p className="font-mono text-[10px] break-all mt-1 text-gray-700">{vfRegistro.huella}</p>
                {vfRegistro.qrUrl ? (
                  <a
                    href={vfRegistro.qrUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-violet-700 underline mt-2 inline-block"
                  >
                    Abrir URL de cotejo en la AEAT
                  </a>
                ) : null}
                <p className="text-[10px] text-gray-500 mt-2">
                  Documentación:{' '}
                  <a
                    href="https://sede.agenciatributaria.gob.es/Sede/iva/sistemas-informaticos-facturacion-verifactu/informacion-tecnica.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-violet-700 underline"
                  >
                    Información técnica AEAT
                  </a>
                </p>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {invoice.id ? (
        <InvoicePdfPreviewModal
          invoiceId={invoice.id}
          invoiceNumber={invoice.number}
          open={pdfPreviewOpen}
          onClose={() => setPdfPreviewOpen(false)}
        />
      ) : null}
    </div>
  );
};

